/**
 * Runtime-tunable configuration for the ads pipeline. Mirrors
 * `AdsConfig` from `@highnote/server/src/core/entities` and is kept
 * intentionally duplicated so the client never pulls a server-only
 * module into the bundle. Keep the shape in lock-step with the server.
 *
 * The server may return a partial document — `parseAdsConfig` below
 * fills missing or malformed fields from `DEFAULT_ADS_CONFIG` so a
 * consumer never sees an invalid config.
 */
export type AdsConfig = {
  /** Master kill switch for carousel ads. */
  carouselEnabled: boolean;
  /** Inject an ad every N comments. Must be > 0. */
  carouselInterval: number;
  /** Require at least N comments before the first ad is injected. */
  carouselMinBefore: number;
  /** Master kill switch for pinned space-item ads. */
  spacePinnedEnabled: boolean;
  /** Maximum number of pinned ads returned/rendered per request. */
  spacePinnedMax: number;
};

/**
 * Safe defaults applied when the server config doc is missing or when a
 * field fails type validation. The defaults here must match
 * `DEFAULT_ADS_CONFIG` on the server; they are duplicated only because
 * the client cannot import from `@highnote/server/src/core/entities`
 * without pulling server-only code into the bundle.
 */
export const DEFAULT_ADS_CONFIG: AdsConfig = {
  carouselEnabled: true,
  carouselInterval: 5,
  carouselMinBefore: 3,
  spacePinnedEnabled: true,
  spacePinnedMax: 2,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * Coerces an unknown server payload into a fully-populated [AdsConfig].
 * Each field is validated independently; any field that fails validation
 * falls back to the corresponding value in [DEFAULT_ADS_CONFIG]. This
 * means a partial or malformed doc never blows up the client — callers
 * always get a usable config.
 *
 * Validation rules:
 *   - boolean fields must be exactly `true`/`false` (no truthy coercion).
 *   - `carouselInterval` must be a positive number (> 0).
 *   - `carouselMinBefore` must be a non-negative number (>= 0).
 *   - `spacePinnedMax` must be a non-negative number (>= 0).
 */
export const parseAdsConfig = (input: unknown): AdsConfig => {
  if (!isRecord(input)) return { ...DEFAULT_ADS_CONFIG };

  const carouselEnabled =
    typeof input.carouselEnabled === "boolean"
      ? input.carouselEnabled
      : DEFAULT_ADS_CONFIG.carouselEnabled;

  const carouselInterval =
    typeof input.carouselInterval === "number" &&
    Number.isFinite(input.carouselInterval) &&
    input.carouselInterval > 0
      ? input.carouselInterval
      : DEFAULT_ADS_CONFIG.carouselInterval;

  const carouselMinBefore =
    typeof input.carouselMinBefore === "number" &&
    Number.isFinite(input.carouselMinBefore) &&
    input.carouselMinBefore >= 0
      ? input.carouselMinBefore
      : DEFAULT_ADS_CONFIG.carouselMinBefore;

  const spacePinnedEnabled =
    typeof input.spacePinnedEnabled === "boolean"
      ? input.spacePinnedEnabled
      : DEFAULT_ADS_CONFIG.spacePinnedEnabled;

  const spacePinnedMax =
    typeof input.spacePinnedMax === "number" &&
    Number.isFinite(input.spacePinnedMax) &&
    input.spacePinnedMax >= 0
      ? input.spacePinnedMax
      : DEFAULT_ADS_CONFIG.spacePinnedMax;

  return {
    carouselEnabled,
    carouselInterval,
    carouselMinBefore,
    spacePinnedEnabled,
    spacePinnedMax,
  };
};
